/**
 * Cliente de la vertical de Restaurante: zonas, mesas, comandas concurrentes, KDS y día operativo.
 */

import { apiRequest } from "./api";

export type TableStatus =
  | "available"
  | "occupied"
  | "bill_printed"
  | "dirty"
  | "reserved"
  | "out_of_service";

export type CheckStatus = "open" | "billed" | "closed" | "void";
export type KitchenTicketStatus = "pending" | "preparing" | "ready" | "served" | "cancelled";
export type LineStatus = "draft" | "sent" | "preparing" | "ready" | "served" | "void";

export interface ZonePublic {
  id: string;
  branch_id: string;
  name: string;
  position: number;
  is_active: boolean;
}

export interface TablePublic {
  id: string;
  branch_id: string;
  zone_id: string | null;
  code: string;
  seats: number;
  status: TableStatus;
  position_x: number;
  position_y: number;
  is_active: boolean;
}

export interface KitchenAreaPublic {
  id: string;
  name: string;
  position: number;
  target_minutes: number;
  is_active: boolean;
}

export interface ModifierPublic {
  id: string;
  name: string;
  price_delta_cents: number;
  product_id: string | null;
  is_active: boolean;
}

export interface CheckLinePublic {
  id: string;
  check_id: string;
  variant_id: string;
  sale_id: string | null;
  kitchen_ticket_id: string | null;
  sku: string;
  name: string;
  quantity: number | string;
  unit_price_cents: number;
  modifiers_cents: number;
  discount_cents: number;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  tax_code: string;
  sat_code: string;
  sat_factor: string;
  tax_rate: number | string;
  seat: number | null;
  course: number;
  notes: string | null;
  status: LineStatus;
  modifiers: string[];
}

export interface CheckPublic {
  id: string;
  branch_id: string;
  table_id: string | null;
  opened_by: string | null;
  closed_by: string | null;
  waiter_id: string | null;
  series: string;
  folio: number;
  status: CheckStatus;
  origin: string;
  label: string | null;
  guests: number;
  customer_name: string | null;
  customer_phone: string | null;
  delivery_address: string | null;
  business_date: string;
  opened_at: string;
  bill_requested_at: string | null;
  closed_at: string | null;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  paid_cents: number;
  tip_cents: number;
  service_charge_cents: number;
  prices_include_tax: boolean;
  currency: string;
  sale_id: string | null;
  notes: string | null;
}

export interface CheckDetailPublic {
  check: CheckPublic;
  lines: CheckLinePublic[];
}

export interface KitchenTicketPublic {
  id: string;
  check_id: string;
  area_id: string;
  number: number;
  status: KitchenTicketStatus;
  table_code: string | null;
  waiter_name: string | null;
  course: number;
  sent_at: string;
  started_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  notes: string | null;
  lines: CheckLinePublic[];
}

export interface FloorMapTable extends TablePublic {
  active_checks_count: number;
  total_cents: number;
}

export interface FloorMapZone extends ZonePublic {
  tables: FloorMapTable[];
}

export interface FloorMapResponse {
  zones: FloorMapZone[];
  unassigned_tables: FloorMapTable[];
}

export interface BusinessDayPublic {
  id: string;
  branch_id: string;
  business_date: string;
  cutoff_hour: number;
  status: string;
  opened_at: string;
  closed_at: string | null;
  notes: string | null;
  snapshot: Record<string, unknown> | null;
}

export const TABLE_STATUS_LABEL: Record<TableStatus, string> = {
  available: "Libre",
  occupied: "Ocupada",
  bill_printed: "Cuenta pedida",
  dirty: "Por limpiar",
  reserved: "Reservada",
  out_of_service: "Fuera de servicio",
};

export const TABLE_STATUS_TONE: Record<TableStatus, "ok" | "danger" | "warning" | "neutral" | "info"> = {
  available: "ok",
  occupied: "danger",
  bill_printed: "warning",
  dirty: "neutral",
  reserved: "info",
  out_of_service: "neutral",
};

export const KITCHEN_TICKET_LABEL: Record<KitchenTicketStatus, string> = {
  pending: "Pendiente",
  preparing: "En preparación",
  ready: "Listo en barra",
  served: "Servido",
  cancelled: "Cancelado",
};

export const KITCHEN_TICKET_TONE: Record<KitchenTicketStatus, "ok" | "warning" | "danger" | "info" | "neutral"> = {
  pending: "warning",
  preparing: "info",
  ready: "ok",
  served: "neutral",
  cancelled: "danger",
};

const BASE = "/api/v1/restaurant";

// Zonas y Mesas
export function listZones(branchId?: string): Promise<{ items: ZonePublic[]; total: number }> {
  const query = branchId ? `?branch_id=${branchId}` : "";
  return apiRequest(`${BASE}/zones${query}`);
}

export function createZone(payload: { branch_id: string; name: string; position?: number }): Promise<ZonePublic> {
  return apiRequest(`${BASE}/zones`, { method: "POST", body: payload });
}

export function listTables(params?: { branch_id?: string; zone_id?: string }): Promise<{ items: TablePublic[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.branch_id) q.set("branch_id", params.branch_id);
  if (params?.zone_id) q.set("zone_id", params.zone_id);
  return apiRequest(`${BASE}/tables?${q.toString()}`);
}

export function createTable(payload: {
  branch_id: string;
  zone_id?: string | null;
  code: string;
  seats?: number;
  position_x?: number;
  position_y?: number;
}): Promise<TablePublic> {
  return apiRequest(`${BASE}/tables`, { method: "POST", body: payload });
}

export function updateTableStatus(tableId: string, status: TableStatus): Promise<TablePublic> {
  return apiRequest(`${BASE}/tables/${tableId}/status`, { method: "PATCH", body: { status } });
}

export function getFloorMap(branchId: string): Promise<FloorMapResponse> {
  return apiRequest(`${BASE}/floor-map?branch_id=${branchId}`);
}

// Modificadores y Cocinas
export function listModifiers(productId?: string): Promise<ModifierPublic[]> {
  const q = productId ? `?product_id=${productId}` : "";
  return apiRequest(`${BASE}/modifiers${q}`);
}

export function createModifier(payload: { name: string; price_delta_cents: number; product_id?: string | null }): Promise<ModifierPublic> {
  return apiRequest(`${BASE}/modifiers`, { method: "POST", body: payload });
}

export function listKitchenAreas(): Promise<KitchenAreaPublic[]> {
  return apiRequest(`${BASE}/areas`);
}

export function createKitchenArea(payload: { name: string; position?: number; target_minutes?: number }): Promise<KitchenAreaPublic> {
  return apiRequest(`${BASE}/areas`, { method: "POST", body: payload });
}

// Cuentas / Comandas
export function listChecks(params?: {
  branch_id?: string;
  table_id?: string;
  statuses?: string[];
}): Promise<{ items: CheckPublic[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.branch_id) q.set("branch_id", params.branch_id);
  if (params?.table_id) q.set("table_id", params.table_id);
  if (params?.statuses) {
    for (const s of params.statuses) q.append("statuses", s);
  }
  return apiRequest(`${BASE}/checks?${q.toString()}`);
}

export function getCheck(checkId: string): Promise<CheckDetailPublic> {
  return apiRequest(`${BASE}/checks/${checkId}`);
}

export function openCheck(payload: {
  branch_id: string;
  table_id?: string | null;
  label?: string | null;
  guests?: number;
  customer_name?: string | null;
  customer_phone?: string | null;
}): Promise<CheckDetailPublic> {
  return apiRequest(`${BASE}/checks`, { method: "POST", body: payload });
}

export function addLineToCheck(
  checkId: string,
  payload: {
    variant_id: string;
    quantity: number;
    unit_price_cents?: number | null;
    modifier_ids?: string[];
    seat?: number | null;
    course?: number;
    notes?: string | null;
  },
): Promise<CheckDetailPublic> {
  return apiRequest(`${BASE}/checks/${checkId}/lines`, { method: "POST", body: payload });
}

export function voidLine(checkId: string, lineId: string, reason: string): Promise<CheckDetailPublic> {
  return apiRequest(`${BASE}/checks/${checkId}/lines/${lineId}`, { method: "DELETE", body: { reason } });
}

export function sendToKitchen(checkId: string, lineIds?: string[]): Promise<CheckDetailPublic> {
  return apiRequest(`${BASE}/checks/${checkId}/send-to-kitchen`, { method: "POST", body: { line_ids: lineIds ?? null } });
}

export function requestBill(checkId: string): Promise<CheckDetailPublic> {
  return apiRequest(`${BASE}/checks/${checkId}/bill`, { method: "POST", body: {} });
}

export interface PaymentPayload {
  method: string;
  amount_cents: number;
  reference?: string | null;
}

export function chargeCheck(
  checkId: string,
  payload: {
    payments: PaymentPayload[];
    line_ids?: string[] | null;
    tip_cents?: number;
    cash_session_id?: string | null;
    warehouse_id?: string | null;
    currency?: string;
  },
): Promise<{ status: string; sale_id: string | null; paid_cents: number; tip_cents: number }> {
  return apiRequest(`${BASE}/checks/${checkId}/charge`, { method: "POST", body: payload });
}

export function cancelCheck(checkId: string, reason: string): Promise<{ status: string; message: string }> {
  return apiRequest(`${BASE}/checks/${checkId}/cancel`, { method: "POST", body: { reason } });
}

// KDS
export function getKdsBoard(areaId: string): Promise<KitchenTicketPublic[]> {
  return apiRequest(`${BASE}/kds/board/${areaId}`);
}

export function updateKitchenTicketStatus(ticketId: string, status: KitchenTicketStatus): Promise<KitchenTicketPublic> {
  return apiRequest(`${BASE}/kds/tickets/${ticketId}/status`, { method: "PATCH", body: { status } });
}

// Día operativo
export function getCurrentBusinessDay(branchId: string): Promise<BusinessDayPublic> {
  return apiRequest(`${BASE}/business-day/current?branch_id=${branchId}`);
}

export function closeBusinessDay(payload: { branch_id: string; business_date?: string | null; notes?: string | null }): Promise<Record<string, unknown>> {
  return apiRequest(`${BASE}/business-day/close`, { method: "POST", body: payload });
}

